import { useState } from 'react'
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { adminApi, type BookingEnquiryDto, type EnquiryStatus, type TrainerRole } from '@/api/admin'
import { CheckCircle2, XCircle, MessageSquare, ChevronDown, ChevronUp, CalendarPlus, AlertTriangle } from 'lucide-react'
import { VenueSelector } from '@/features/admin/venues/VenueSelector'
import { Label } from '@/components/ui/label'
import { NoEligibleTrainers } from '@/components/shared/NoEligibleTrainers'
import { useAdminDates } from '@/hooks/useDates'
import { formatTrainerConflict } from '@/lib/trainerConflicts'

const STATUS_CONFIG: Record<EnquiryStatus, { label: string; cls: string }> = {
  Pending:      { label: 'Pending',      cls: 'bg-amber-100 text-amber-700' },
  Acknowledged: { label: 'Acknowledged', cls: 'bg-blue-100 text-blue-700' },
  Converted:    { label: 'Booking created', cls: 'bg-green-100 text-green-700' },
  Declined:     { label: 'Declined',     cls: 'bg-gray-100 text-gray-500' },
}

type TrainerAssignment = { trainerId: string; role: TrainerRole }

function CreateBookingForm({
  enquiry,
  onCreated,
  onCancel,
}: {
  enquiry: BookingEnquiryDto
  onCreated: (clientId: string) => void
  onCancel: () => void
}) {
  const queryClient = useQueryClient()

  const [bookingDate, setBookingDate] = useState(enquiry.preferredDate ?? '')
  const [places, setPlaces] = useState(enquiry.places)
  const [notes, setNotes] = useState('')
  const [venueId, setVenueId] = useState<string | null>(null)
  const [trainerAssignments, setTrainerAssignments] = useState<TrainerAssignment[]>([])

  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })

  const eligibleTrainers = trainers.filter(t =>
    t.isActive && (!enquiry.courseId || t.qualifiedCourseIds.includes(enquiry.courseId))
  )

  const assignedTrainerIds = bookingDate ? trainerAssignments.map(a => a.trainerId) : []
  const conflictQueries = useQueries({
    queries: assignedTrainerIds.map(tid => ({
      queryKey: ['trainer-conflicts', tid, bookingDate, undefined],
      queryFn: () => adminApi.getTrainerConflicts(tid, bookingDate).then(r => r.data),
      enabled: !!bookingDate,
    })),
  })
  const conflictMap: Record<string, string[]> = {}
  assignedTrainerIds.forEach((tid, i) => {
    const conflicts = conflictQueries[i].data ?? []
    if (conflicts.length > 0) conflictMap[tid] = conflicts.map(formatTrainerConflict)
  })

  const setTrainerRole = (trainerId: string, role: TrainerRole | null) => {
    setTrainerAssignments(prev => {
      const without = prev.filter(a => a.trainerId !== trainerId)
      if (role === 'Lead') {
        const demoted = without.map(a => a.role === 'Lead' ? { ...a, role: 'Assistant' as TrainerRole } : a)
        return [...demoted, { trainerId, role }]
      }
      if (role === 'Assistant') {
        const effectiveRole: TrainerRole = without.some(a => a.role === 'Lead') ? 'Assistant' : 'Lead'
        return [...without, { trainerId, role: effectiveRole }]
      }
      return without
    })
  }
  const getRole = (tid: string): TrainerRole | null =>
    trainerAssignments.find(a => a.trainerId === tid)?.role ?? null

  const staffingWarning = trainerAssignments.length > 0 && !trainerAssignments.some(a => a.role === 'Lead')
    ? 'No lead trainer assigned.' : null

  const convertMutation = useMutation({
    mutationFn: () => adminApi.convertEnquiry(enquiry.id, {
      bookingDate,
      places,
      status: 'Booked',
      notes: notes.trim() || null,
      venueId,
      trainerAssignments,
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['booking-enquiries'] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['client-bookings', enquiry.clientId] })
      onCreated(res.data.clientId)
    },
  })

  const fieldCls = "w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-4">
      <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
        <CalendarPlus size={15} /> Create booking from this enquiry
      </p>

      {!enquiry.hasCourse && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          This enquiry has no linked course — a booking cannot be created automatically.
          Please create the booking manually from the client page.
        </p>
      )}

      {enquiry.hasCourse && (
        <>
          {/* Date + Attendees */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Booking date</label>
              <input type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)}
                required className={fieldCls} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Attendees</label>
              <input type="number" min={1} value={places === 0 ? '' : places}
                onChange={e => { const v = e.target.value; setPlaces(v === '' ? 0 : parseInt(v) || 0) }}
                onBlur={() => setPlaces(p => Math.max(1, p))}
                className={fieldCls} />
            </div>
          </div>

          {/* Requested staff chips */}
          {enquiry.requestedStaffNames?.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Requested staff</label>
              <div className="flex flex-wrap gap-1.5">
                {enquiry.requestedStaffNames.map(name => (
                  <span key={name} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Notes (optional)</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any booking notes…" className={fieldCls} />
          </div>

          {/* Venue */}
          <VenueSelector value={venueId} onChange={setVenueId} />

          {/* Trainer assignment */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-600">Trainer assignment</Label>
            {eligibleTrainers.length === 0 ? (
              <NoEligibleTrainers
                hasCourse={!!enquiry.courseId}
                hasAnyTrainers={trainers.some(t => t.isActive)}
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {eligibleTrainers.map(t => {
                  const role = getRole(t.id)
                  const conflict = conflictMap[t.id]
                  const assigned = role !== null
                  const isLead = role === 'Lead'
                  return (
                    <div key={t.id} className={`rounded-lg border p-2.5 transition-colors ${assigned ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-gray-800 truncate">{t.firstName} {t.lastName}</span>
                        {conflict && (
                          <span title={`Double-booked: ${conflict.join('; ')}`}>
                            <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setTrainerRole(t.id, assigned ? null : 'Assistant')}
                          className={`flex-1 text-xs py-0.5 rounded border cursor-pointer transition-colors ${
                            assigned
                              ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {assigned ? 'Assigned ✓' : 'Assign'}
                        </button>
                        {assigned && (
                          <button
                            type="button"
                            onClick={() => setTrainerRole(t.id, isLead ? 'Assistant' : 'Lead')}
                            title={isLead ? 'Remove lead role' : 'Set as lead trainer'}
                            className={`text-sm px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${
                              isLead
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-500'
                            }`}
                          >
                            ★
                          </button>
                        )}
                      </div>
                      {conflict && (
                        <p className="text-[10px] text-amber-600 mt-1 truncate" title={conflict.join(', ')}>⚠ Double-booked this day</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            {staffingWarning && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} /> {staffingWarning}
              </p>
            )}
          </div>

          {convertMutation.isError && (
            <p className="text-xs text-red-600">Something went wrong. Please try again.</p>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => convertMutation.mutate()}
              disabled={!bookingDate || convertMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 transition-colors cursor-pointer"
            >
              <CalendarPlus size={14} />
              {convertMutation.isPending ? 'Creating…' : 'Create booking'}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {!enquiry.hasCourse && (
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}

function EnquiryRow({ enquiry }: { enquiry: BookingEnquiryDto }) {
  const { formatDate, formatDateTime } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const fmtDateTime = (d: string) => {
    return formatDateTime(d, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  }
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(enquiry.status === 'Pending')
  const [notes, setNotes] = useState(enquiry.adminNotes ?? '')
  const [showConvertForm, setShowConvertForm] = useState(false)

  const notesDirty = notes !== (enquiry.adminNotes ?? '')

  const updateMutation = useMutation({
    mutationFn: (status: EnquiryStatus) =>
      adminApi.updateEnquiryStatus(enquiry.id, status, notes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['booking-enquiries'] }),
  })

  const saveNotesMutation = useMutation({
    mutationFn: () => adminApi.updateEnquiryStatus(enquiry.id, enquiry.status, notes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['booking-enquiries'] }),
  })

  const cfg = STATUS_CONFIG[enquiry.status]

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      enquiry.status === 'Pending' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-white'
    }`}>
      {/* Summary row */}
      <div
        className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-black/[0.02] transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{enquiry.clientName}</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-700">{enquiry.courseName}</span>
            {enquiry.preferredDate && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500 text-sm">{fmtDate(enquiry.preferredDate)}</span>
              </>
            )}
            <span className="text-gray-400">·</span>
            <span className="text-sm text-gray-500">{enquiry.places} {enquiry.places === 1 ? 'attendee' : 'attendees'}</span>
          </div>
          {enquiry.requestedStaffNames?.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              Staff requested: {enquiry.requestedStaffNames.join(', ')}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">{fmtDateTime(enquiry.createdAt)}</p>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 ${cfg.cls}`}>
          {cfg.label}
        </span>
        {expanded
          ? <ChevronUp size={15} className="text-gray-400 shrink-0" />
          : <ChevronDown size={15} className="text-gray-400 shrink-0" />}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4">
          {enquiry.message && (
            <div className="flex gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2.5">
              <MessageSquare size={14} className="text-gray-400 mt-0.5 shrink-0" />
              <p className="whitespace-pre-wrap">{enquiry.message}</p>
            </div>
          )}

          {/* Admin notes */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Notes to client</label>
              {notesDirty && (
                <button
                  onClick={() => saveNotesMutation.mutate()}
                  disabled={saveNotesMutation.isPending}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-60 cursor-pointer transition-colors"
                >
                  {saveNotesMutation.isPending ? 'Saving…' : 'Save note'}
                </button>
              )}
            </div>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add a message for the client (shown on their enquiry)…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          {/* Create booking form */}
          {showConvertForm && (
            <CreateBookingForm
              enquiry={enquiry}
              onCreated={(clientId) => {
                setShowConvertForm(false)
                navigate(`/admin/clients/${clientId}?tab=bookings`)
              }}
              onCancel={() => setShowConvertForm(false)}
            />
          )}

          {/* Action buttons */}
          {!showConvertForm && (
            <div className="flex flex-wrap gap-2 items-center">
              {enquiry.status !== 'Acknowledged' && enquiry.status !== 'Converted' && (
                <button
                  onClick={() => updateMutation.mutate('Acknowledged')}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors cursor-pointer"
                >
                  <CheckCircle2 size={14} /> Acknowledge
                </button>
              )}
              {enquiry.status !== 'Converted' && (
                <button
                  onClick={() => setShowConvertForm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors cursor-pointer"
                >
                  <CalendarPlus size={14} /> Create booking
                </button>
              )}
              {enquiry.status !== 'Declined' && enquiry.status !== 'Converted' && (
                <button
                  onClick={() => updateMutation.mutate('Declined')}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition-colors cursor-pointer"
                >
                  <XCircle size={14} /> Decline
                </button>
              )}
              {updateMutation.isPending && <span className="text-xs text-gray-400">Saving…</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function EnquiriesTab() {
  const { data: enquiries = [], isLoading } = useQuery({
    queryKey: ['booking-enquiries'],
    queryFn: () => adminApi.getEnquiries().then(r => r.data),
  })

  const pending      = enquiries.filter(e => e.status === 'Pending')
  const acknowledged = enquiries.filter(e => e.status === 'Acknowledged')
  const closed       = enquiries.filter(e => e.status === 'Converted' || e.status === 'Declined')

  if (isLoading) return <p className="text-gray-500 py-6">Loading…</p>

  if (enquiries.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400 text-sm">
        No booking enquiries yet.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Needs action ({pending.length})
          </h3>
          {pending.map(e => <EnquiryRow key={e.id} enquiry={e} />)}
        </section>
      )}
      {acknowledged.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider">
            Acknowledged — awaiting follow-up ({acknowledged.length})
          </h3>
          {acknowledged.map(e => <EnquiryRow key={e.id} enquiry={e} />)}
        </section>
      )}
      {closed.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Closed</h3>
          {closed.map(e => <EnquiryRow key={e.id} enquiry={e} />)}
        </section>
      )}
    </div>
  )
}
